/**
 * particles.ts
 *
 * A GPU-drawn (CPU-advected) particle system that samples a wind field
 * (real NOAA GFS data by default; see main.ts for the idealized fallback)
 * at each particle's current (lat, lon, altitude), advects it, and renders
 * a short fading motion trail per particle (like the classic
 * earth.nullschool wind animation) colored by temperature.
 *
 * Altitude bookkeeping (important): each particle tracks a TRUE altitude
 * (real km, evolved from the real, unexaggerated vertical wind) which is
 * what gets used to look up pressure/wind/temperature -- this keeps the
 * physics internally consistent regardless of visual exaggeration. For
 * rendering, we also track a slow-following "baseline" altitude; vertical
 * motion exaggeration amplifies only the (small) deviation of the true
 * altitude from that baseline, and the result is scaled by the SAME height
 * exaggeration used by the terrain and atmosphere shells. That means a
 * particle's baseline position always matches the terrain/atmosphere scale
 * exactly, while vertical motion exaggeration purely controls how visible
 * its wobble around that baseline is -- the two no longer compound.
 *
 * Particle population density is kept roughly uniform over the globe (even
 * though advection naturally clumps particles into convergence zones) by
 * biasing respawns toward under-populated regions and retiring particles
 * early out of significantly over-populated regions -- EXCEPT over
 * low-pressure systems, where a higher density is deliberately induced
 * instead, since that's exactly where convergent/spiraling advection is
 * most interesting to see; see particleDensityGrid.ts. Individual particles
 * ALSO get a (deeper-pressure = longer) density-retirement immunity window
 * from their own precise local MSLP, independent of the coarser per-cell
 * boost, so small/tight storms the density grid's cells are too coarse to
 * resolve still hold onto particles long enough to show their structure.
 *
 * Near the terrain surface, a particle's display radius is smoothly (not abruptly) decelerated
 * toward a floor just above the local terrain -- see currentWorldPosition()'s softFloor() --
 * so vertical motion exaggeration never visually sinks a particle into the globe, and doesn't
 * produce a "crashes into the ground, then suddenly slides" discontinuity either (a hard clamp
 * would stop vertical motion instantly while horizontal advection keeps going unaffected).
 * Particles resting at that floor for too long are still retired gracefully rather than left
 * lingering there indefinitely -- see GROUNDED_RETIRE_SECONDS.
 */

import * as THREE from "three";
import type { MslpSamplerFn, WindSamplerFn } from "../physics/windSampler";
import { pressureToHeightM, heightMToPressureHpa } from "../physics/standardAtmosphere";
import { temperatureToColor } from "../colorScales";
import { latLonToUnitVector } from "./geometryUtils";
import { ParticleDensityGrid } from "./particleDensityGrid";
import {
  ALTITUDE_OUT_OF_BOUNDS_MARGIN_KM,
  ALTITUDE_SMOOTHING_TAU_SECONDS,
  DENSITY_GRID_BASE_LON_BINS,
  DENSITY_GRID_LAT_BANDS,
  DENSITY_GRID_RECOMPUTE_EVERY_N_FRAMES,
  EARTH_RADIUS_UNITS,
  GROUNDED_PROXIMITY_FRACTION,
  GROUNDED_RETIRE_SECONDS,
  HEAD_POINT_MAX_SIZE_PX,
  HEAD_POINT_MIN_SIZE_PX,
  HEAD_POINT_REFERENCE_SPEED_MS,
  KM_PER_SCENE_UNIT,
  LOW_PRESSURE_PERSISTENCE_FULL_HPA,
  LOW_PRESSURE_PERSISTENCE_START_HPA,
  MAX_PERSISTENCE_SECONDS,
  MIN_PERSISTENCE_SECONDS,
  OVERLOAD_RETIRE_RATE_PER_SECOND,
  PARTICLE_COUNT,
  PARTICLE_FADE_IN_SECONDS,
  PARTICLE_FADE_OUT_SECONDS,
  PARTICLE_MAX_AGE_SECONDS,
  PARTICLE_MAX_OPACITY,
  PARTICLE_TERRAIN_CLEARANCE_UNITS,
  PARTICLE_TERRAIN_SOFT_FLOOR_KM,
  PARTICLE_TRAIL_LENGTH,
  MIN_ALTITUDE_OPACITY_FACTOR,
  PERSISTENCE_SAMPLE_STAGGER_FRAMES,
  SIMULATION_TIME_SCALE,
  TRAIL_WIDTH_FRACTION_OF_HEAD_SIZE,
  WZ_SMOOTHING_TAU_SECONDS
} from "../constants";

const EARTH_RADIUS_M = 6_371_000;
const RAD2DEG = 180 / Math.PI;
const DEG2RAD = Math.PI / 180;
const MIN_COS_LAT = 0.05; // avoids a divide-by-near-zero blowup for longitude speed near the poles
const SEGMENTS_PER_PARTICLE = PARTICLE_TRAIL_LENGTH - 1;

/** Shared by the head marker and the trail (whose width is a fraction of it) so both read as
 *  one continuous shape per particle rather than a mismatched fat dot on a thin tail. */
function headSizeForSpeedPx(speedMs: number): number {
  const speedT = HEAD_POINT_REFERENCE_SPEED_MS / (speedMs + HEAD_POINT_REFERENCE_SPEED_MS);
  return HEAD_POINT_MIN_SIZE_PX + (HEAD_POINT_MAX_SIZE_PX - HEAD_POINT_MIN_SIZE_PX) * speedT;
}

// Trail-age fade curve (an eased, square-root-like taper -- see writeTrailGeometry()) depends
// only on the small integer slot age (0..PARTICLE_TRAIL_LENGTH-1), which is IDENTICAL for every
// particle at a given age. Precomputed once here instead of calling the transcendental
// Math.pow() for every (particle, segment-end) pair every frame (count * SEGMENTS_PER_PARTICLE
// * 2 calls/frame -- hundreds of thousands -- for what is really only PARTICLE_TRAIL_LENGTH
// distinct values).
const AGE_FADE_CURVE = (() => {
  const curve = new Float32Array(PARTICLE_TRAIL_LENGTH);
  for (let age = 0; age < PARTICLE_TRAIL_LENGTH; age++) {
    curve[age] = Math.pow(1 - age / SEGMENTS_PER_PARTICLE, 0.45);
  }
  return curve;
})();

export interface ParticleUpdateConfig {
  heightExaggeration: number;
  verticalMotionExaggeration: number;
  minHpa: number;
  maxHpa: number;
  speedMultiplier: number;
  /** Renderer's device pixel ratio, so head markers/trails render at a consistent CSS-pixel size. */
  devicePixelRatio: number;
  /** Renderer's drawing-buffer size in device pixels, needed to size trail quads in screen space. */
  viewportWidthPx: number;
  viewportHeightPx: number;
}

export interface ParticleSystemHandles {
  object3D: THREE.Object3D;
  update(dtSeconds: number, config: ParticleUpdateConfig): void;
}

export function createParticleSystem(
  sampleWind: WindSamplerFn,
  sampleMslpHpa?: MslpSamplerFn,
  elevationMetersAt?: (latDeg: number, lonDeg: number) => number
): ParticleSystemHandles {
  const count = PARTICLE_COUNT;
  const lonDeg = new Float32Array(count);
  const latDeg = new Float32Array(count);
  const trueAltKm = new Float32Array(count); // real, unexaggerated altitude -- used for physics sampling
  const baselineAltKm = new Float32Array(count); // slow-following reference for the display wobble
  const smoothedWMs = new Float32Array(count); // low-pass-filtered vertical velocity (see WZ_SMOOTHING_TAU_SECONDS)
  const ageSeconds = new Float32Array(count);
  const temperatureK = new Float32Array(count);
  const horizontalSpeedMs = new Float32Array(count); // used to size the head marker (see constants.ts)
  // How long (consecutive seconds) a particle has been pinned against the terrain-clip floor --
  // see currentWorldPosition(). Exceeding GROUNDED_RETIRE_SECONDS triggers a (graceful, faded)
  // retirement instead of letting it visually sit flattened against a mountainside indefinitely.
  const groundedSeconds = new Float32Array(count);
  // Seconds remaining during which this particle is immune to density-based ("overloaded")
  // early retirement, (re-)granted while it sits in a low-pressure zone -- see
  // LOW_PRESSURE_PERSISTENCE_* in constants.ts and the update() loop below.
  const persistenceSecondsRemaining = new Float32Array(count);
  const fadingOut = new Uint8Array(count); // 1 while a particle's trail is fading before it jumps to a new spot
  const fadeOutProgress = new Float32Array(count); // 0..1 while fadingOut[i]
  const fadingIn = new Uint8Array(count); // 1 while a just-(re)spawned particle is still fading in
  const fadeInProgress = new Float32Array(count); // 0..1 while fadingIn[i]
  // Combined fade-in/fade-out opacity multiplier (0..1), computed once per particle per frame in
  // update() and reused by both writeHeadPoints() and writeTrailGeometry() so the two don't need
  // to duplicate the same lifecycle logic.
  const lifecycleAlpha = new Float32Array(count);
  // Opacity multiplier (0..1) from this particle's height within the currently selected
  // pressure range -- 1 at the bottom, fading toward MIN_ALTITUDE_OPACITY_FACTOR at the top, so
  // higher/thinner air reads as visually more ethereal. Also computed once per frame in update().
  const altitudeOpacity = new Float32Array(count).fill(1);

  const densityGrid = new ParticleDensityGrid(DENSITY_GRID_LAT_BANDS, DENSITY_GRID_BASE_LON_BINS, sampleMslpHpa);
  // Counts frames so the density-grid rebuild and per-particle MSLP persistence sampling below
  // can each be throttled/staggered across frames instead of running for every particle every
  // single frame -- both are O(particle count) and became real costs at large particle counts.
  let frameCounter = 0;

  // Ring buffer of recent world-space positions per particle, used to draw the fading trail.
  const history = new Float32Array(count * PARTICLE_TRAIL_LENGTH * 3);
  // Parallel ring buffer recording the (precomputed, linear-space RGB) color of the temperature
  // AT the time/place each history point was recorded, rather than the particle's current
  // temperature, so a trail visibly shows the thermal history it actually passed through, e.g.
  // cooling as it crosses a front. The color is precomputed here (written once, whenever a slot
  // is recorded) rather than in writeTrailGeometry() (read every frame) because each slot would
  // otherwise have its color recomputed on every single frame it remains in the trail (up to
  // PARTICLE_TRAIL_LENGTH times) instead of exactly once.
  const historyColor = new Float32Array(count * PARTICLE_TRAIL_LENGTH * 3);
  const headIndex = new Uint8Array(count);

  const tmpVec = new THREE.Vector3();
  const tmpColor = new THREE.Color();
  // Set by currentWorldPosition() each time it's called; read immediately afterward by the main
  // physics loop to track how long a particle has been resting at the terrain soft floor below
  // (see groundedSeconds / GROUNDED_RETIRE_SECONDS).
  let lastPositionWasNearFloor = false;

  /** The altitude actually used for rendering: baseline + exaggerated deviation from it. */
  function displayAltKm(i: number, verticalMotionExaggeration: number): number {
    const deviation = trueAltKm[i] - baselineAltKm[i];
    return baselineAltKm[i] + deviation * verticalMotionExaggeration;
  }

  /**
   * Smoothly decelerates `radiusUnits` toward (never reaching, let alone passing) `floorUnits`
   * as it approaches/would go below it, instead of hard-clamping -- see
   * PARTICLE_TERRAIN_SOFT_FLOOR_KM. C1-continuous at the point where the buffer zone begins
   * (both value and slope match the unclamped identity there), so there's no visible kink when
   * a particle first enters the cushioning zone, only a gradual deceleration.
   */
  function softFloor(radiusUnits: number, floorUnits: number, bufferUnits: number): { value: number; nearFloor: boolean } {
    if (bufferUnits <= 0) {
      const clamped = Math.max(radiusUnits, floorUnits);
      return { value: clamped, nearFloor: radiusUnits <= floorUnits };
    }
    const u = (radiusUnits - floorUnits) / bufferUnits;
    if (u >= 1) return { value: radiusUnits, nearFloor: false };
    const value = floorUnits + bufferUnits * Math.exp(u - 1);
    const nearFloor = (value - floorUnits) / bufferUnits < GROUNDED_PROXIMITY_FRACTION;
    return { value, nearFloor };
  }

  function currentWorldPosition(
    i: number,
    heightExaggeration: number,
    verticalMotionExaggeration: number,
    target: THREE.Vector3
  ): THREE.Vector3 {
    let radiusUnits =
      EARTH_RADIUS_UNITS + (displayAltKm(i, verticalMotionExaggeration) * heightExaggeration) / KM_PER_SCENE_UNIT;
    // Standard-atmosphere pressure altitude is relative to sea level, not local ground -- near
    // the surface (high pressure levels), that can put a particle rendered BELOW the terrain
    // mesh wherever the real ground is well above sea level (mountains, plateaus), especially
    // once height exaggeration amplifies the terrain bump far more than a near-zero particle
    // altitude. Soft-floor it (see softFloor() above) to just above the local (equally
    // exaggerated) terrain radius so particles never visually sink into the globe -- and, just
    // as importantly, so their descent visibly DECELERATES into that floor instead of snapping
    // flat while horizontal advection (lat/lon, updated independently every frame) continues
    // unaffected, which otherwise reads as "crashes into the ground, then suddenly slides".
    lastPositionWasNearFloor = false;
    if (elevationMetersAt) {
      const terrainElevKm = elevationMetersAt(latDeg[i], lonDeg[i]) / 1000;
      const terrainRadiusUnits = EARTH_RADIUS_UNITS + (terrainElevKm * heightExaggeration) / KM_PER_SCENE_UNIT;
      const minRadiusUnits = terrainRadiusUnits + PARTICLE_TERRAIN_CLEARANCE_UNITS;
      const bufferUnits = (PARTICLE_TERRAIN_SOFT_FLOOR_KM * heightExaggeration) / KM_PER_SCENE_UNIT;
      const floored = softFloor(radiusUnits, minRadiusUnits, bufferUnits);
      radiusUnits = floored.value;
      lastPositionWasNearFloor = floored.nearFloor;
    }
    return latLonToUnitVector(latDeg[i], lonDeg[i], target).multiplyScalar(radiusUnits);
  }

  function resetTrail(i: number, heightExaggeration: number, verticalMotionExaggeration: number): void {
    currentWorldPosition(i, heightExaggeration, verticalMotionExaggeration, tmpVec);
    headIndex[i] = 0;
    temperatureToColor(temperatureK[i], tmpColor);
    for (let slot = 0; slot < PARTICLE_TRAIL_LENGTH; slot++) {
      const base = (i * PARTICLE_TRAIL_LENGTH + slot) * 3;
      history[base] = tmpVec.x;
      history[base + 1] = tmpVec.y;
      history[base + 2] = tmpVec.z;
      historyColor[base] = tmpColor.r;
      historyColor[base + 1] = tmpColor.g;
      historyColor[base + 2] = tmpColor.b;
    }
  }

  function placeAtRandomLatLon(i: number, minHpa: number, maxHpa: number): void {
    lonDeg[i] = Math.random() * 360 - 180;
    // Sample latitude uniformly in sin(lat) so particles are area-correct on the sphere.
    latDeg[i] = Math.asin(Math.random() * 2 - 1) * RAD2DEG;
    const lowAltKm = pressureToHeightM(maxHpa) / 1000; // maxHpa = larger pressure = lower altitude
    const highAltKm = pressureToHeightM(minHpa) / 1000; // minHpa = smaller pressure = higher altitude
    trueAltKm[i] = lowAltKm + Math.random() * (highAltKm - lowAltKm);
    baselineAltKm[i] = trueAltKm[i];
    smoothedWMs[i] = 0;
  }

  // Immediately samples the wind field at a particle's just-assigned position so its
  // temperature/speed (and therefore its rendered color and head/trail size) are correct from
  // the very first frame it's visible at that spot. Without this, a newly (re)spawned particle
  // keeps whatever stale temperature/speed it had at its OLD location until the next physics
  // pass -- with thousands of particles respawning continuously, that produces a constant
  // "sparkle" of briefly mismatched colors/sizes flashing in at new locations.
  function sampleFreshFieldsAtCurrentPosition(i: number): void {
    const pressureHpa = heightMToPressureHpa(trueAltKm[i] * 1000);
    const wind = sampleWind(latDeg[i], lonDeg[i], pressureHpa);
    temperatureK[i] = wind.temperatureK;
    horizontalSpeedMs[i] = Math.sqrt(wind.uMs * wind.uMs + wind.vMs * wind.vMs);
    smoothedWMs[i] = wind.wMs;
  }

  function respawn(
    i: number,
    minHpa: number,
    maxHpa: number,
    heightExaggeration: number,
    verticalMotionExaggeration: number,
    useDensityGrid: boolean
  ): void {
    if (useDensityGrid) {
      const [sampledLat, sampledLon] = densityGrid.sampleUnderpopulatedLatLon();
      latDeg[i] = sampledLat;
      lonDeg[i] = sampledLon;
      const lowAltKm = pressureToHeightM(maxHpa) / 1000;
      const highAltKm = pressureToHeightM(minHpa) / 1000;
      trueAltKm[i] = lowAltKm + Math.random() * (highAltKm - lowAltKm);
      baselineAltKm[i] = trueAltKm[i];
    } else {
      placeAtRandomLatLon(i, minHpa, maxHpa);
    }
    sampleFreshFieldsAtCurrentPosition(i);
    ageSeconds[i] = Math.random() * PARTICLE_MAX_AGE_SECONDS; // stagger initial ages
    fadingOut[i] = 0;
    fadeOutProgress[i] = 0;
    fadingIn[i] = 1;
    fadeInProgress[i] = 0;
    groundedSeconds[i] = 0;
    persistenceSecondsRemaining[i] = 0;
    resetTrail(i, heightExaggeration, verticalMotionExaggeration);
  }

  for (let i = 0; i < count; i++) {
    placeAtRandomLatLon(i, 10, 1000);
    sampleFreshFieldsAtCurrentPosition(i);
    ageSeconds[i] = Math.random() * PARTICLE_MAX_AGE_SECONDS;
    resetTrail(i, 1, 1);
  }

  // --- Trail geometry (instanced quads) -----------------------------------------------
  // Trails need a WIDTH that varies per particle (to match the head marker -- see below), but
  // WebGL's native line width is capped at ~1px on most platforms/drivers, so each trail
  // segment is instead drawn as a small camera-facing quad ("fat line" technique): a single
  // quad template (4 corners, indices set up once) is instanced once per (particle, trail-age)
  // pair, and the vertex shader offsets each corner perpendicular to the segment in SCREEN
  // SPACE by that particle's current half-width in pixels. This also keeps the per-frame CPU
  // cost down (only 1 set of per-segment attributes is written, not 2x/4x duplicated vertex
  // data) compared to writing a literal quad's worth of vertices every frame.
  const quadCornerArray = new Float32Array([
    0, 1, // older endpoint, + side
    0, -1, // older endpoint, - side
    1, 1, // newer endpoint, + side
    1, -1 // newer endpoint, - side
  ]);
  const quadIndexArray = new Uint16Array([0, 1, 2, 1, 3, 2]);
  const instanceCount = count * SEGMENTS_PER_PARTICLE;

  const geometry = new THREE.InstancedBufferGeometry();
  geometry.setAttribute("corner", new THREE.BufferAttribute(quadCornerArray, 2));
  geometry.setIndex(new THREE.BufferAttribute(quadIndexArray, 1));
  geometry.instanceCount = instanceCount;
  geometry.setAttribute("instanceOlderPos", new THREE.InstancedBufferAttribute(new Float32Array(instanceCount * 3), 3));
  geometry.setAttribute("instanceNewerPos", new THREE.InstancedBufferAttribute(new Float32Array(instanceCount * 3), 3));
  // Separate older/newer colors (rather than one shared color) so a trail shows the
  // temperature it actually passed through at each point, not a single color repainted along
  // its whole length every frame -- see historyColor above.
  geometry.setAttribute("instanceColorOlder", new THREE.InstancedBufferAttribute(new Float32Array(instanceCount * 3), 3));
  geometry.setAttribute("instanceColorNewer", new THREE.InstancedBufferAttribute(new Float32Array(instanceCount * 3), 3));
  geometry.setAttribute("instanceAlphaOlder", new THREE.InstancedBufferAttribute(new Float32Array(instanceCount), 1));
  geometry.setAttribute("instanceAlphaNewer", new THREE.InstancedBufferAttribute(new Float32Array(instanceCount), 1));
  geometry.setAttribute("instanceWidth", new THREE.InstancedBufferAttribute(new Float32Array(instanceCount), 1));
  // The geometry has no literal "position" attribute to compute a bounding volume from (the
  // actual positions live in per-instance attributes the frustum-culling code doesn't inspect),
  // so disable automatic culling -- the trail is always roughly globe-sized and typically at
  // least partly in view anyway.
  const material = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    uniforms: {
      resolution: { value: new THREE.Vector2(1, 1) },
      devicePixelRatio: { value: 1 }
    },
    vertexShader: `
      attribute vec2 corner;
      attribute vec3 instanceOlderPos;
      attribute vec3 instanceNewerPos;
      attribute vec3 instanceColorOlder;
      attribute vec3 instanceColorNewer;
      attribute float instanceAlphaOlder;
      attribute float instanceAlphaNewer;
      attribute float instanceWidth;
      uniform vec2 resolution;
      uniform float devicePixelRatio;
      varying vec3 vColor;
      varying float vAlpha;
      void main() {
        vColor = mix(instanceColorOlder, instanceColorNewer, corner.x);
        vAlpha = mix(instanceAlphaOlder, instanceAlphaNewer, corner.x);

        vec3 selfPos = mix(instanceOlderPos, instanceNewerPos, corner.x);
        vec3 otherPos = mix(instanceNewerPos, instanceOlderPos, corner.x);

        vec4 clipSelf = projectionMatrix * modelViewMatrix * vec4(selfPos, 1.0);
        vec4 clipOther = projectionMatrix * modelViewMatrix * vec4(otherPos, 1.0);

        vec2 ndcSelf = clipSelf.xy / clipSelf.w;
        vec2 ndcOther = clipOther.xy / clipOther.w;
        vec2 screenSelf = ndcSelf * resolution * 0.5;
        vec2 screenOther = ndcOther * resolution * 0.5;

        vec2 dir = screenSelf - screenOther;
        float len = length(dir);
        vec2 normal = len > 0.0001 ? normalize(vec2(-dir.y, dir.x)) : vec2(0.0, 1.0);

        float halfWidthPx = instanceWidth * devicePixelRatio * 0.5;
        vec2 ndcOffset = (normal * halfWidthPx * corner.y) / (resolution * 0.5);

        gl_Position = vec4((ndcSelf + ndcOffset) * clipSelf.w, clipSelf.z, clipSelf.w);
      }
    `,
    fragmentShader: `
      varying vec3 vColor;
      varying float vAlpha;
      void main() {
        gl_FragColor = vec4(vColor, vAlpha);
      }
    `
  });
  const lines = new THREE.Mesh(geometry, material);
  lines.frustumCulled = false;
  // Always draw last/on top of other transparent layers (MSLP shell, coastlines, atmosphere
  // shells), regardless of view angle -- see the render-order comment in globe.ts.
  lines.renderOrder = 10;

  // --- Head marker points ------------------------------------------------------
  // A fast particle's fixed-length trail reads as a long streak; a slow particle's reads as a
  // near-invisible dot. To balance how much visual "space" each particle occupies, a round
  // marker is drawn at each particle's current position, sized inversely to its speed (see
  // HEAD_POINT_*_SIZE_PX in constants.ts).
  const headGeometry = new THREE.BufferGeometry();
  headGeometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(count * 3), 3));
  headGeometry.setAttribute("color", new THREE.BufferAttribute(new Float32Array(count * 3), 3));
  headGeometry.setAttribute("alpha", new THREE.BufferAttribute(new Float32Array(count), 1));
  headGeometry.setAttribute("size", new THREE.BufferAttribute(new Float32Array(count), 1));
  const headMaterial = new THREE.ShaderMaterial({
    vertexColors: true,
    transparent: true,
    depthWrite: false,
    // "size" is a target size in CSS pixels; gl_PointSize is specified in actual framebuffer
    // pixels, so it must be scaled by the renderer's device pixel ratio to render at that
    // visual size. Deliberately NOT distance-attenuated (no projection-based scaling) -- these
    // are UI-style markers meant to stay legible at any zoom level, not world-scale sprites.
    uniforms: { devicePixelRatio: { value: 1 } },
    vertexShader: `
      attribute float alpha;
      attribute float size;
      uniform float devicePixelRatio;
      varying vec3 vColor;
      varying float vAlpha;
      void main() {
        vColor = color;
        vAlpha = alpha;
        gl_PointSize = size * devicePixelRatio;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      varying vec3 vColor;
      varying float vAlpha;
      void main() {
        vec2 fromCenter = gl_PointCoord - vec2(0.5);
        float dist = length(fromCenter);
        if (dist > 0.5) discard;
        float edgeFade = smoothstep(0.5, 0.3, dist);
        gl_FragColor = vec4(vColor, vAlpha * edgeFade);
      }
    `
  });
  const headPoints = new THREE.Points(headGeometry, headMaterial);
  headPoints.renderOrder = 10;

  const object3D = new THREE.Group();
  object3D.add(lines);
  object3D.add(headPoints);

  function writeHeadPoints(devicePixelRatio: number): void {
    headMaterial.uniforms.devicePixelRatio.value = devicePixelRatio;
    const posAttr = headGeometry.getAttribute("position") as THREE.BufferAttribute;
    const colorAttr = headGeometry.getAttribute("color") as THREE.BufferAttribute;
    const alphaAttr = headGeometry.getAttribute("alpha") as THREE.BufferAttribute;
    const sizeAttr = headGeometry.getAttribute("size") as THREE.BufferAttribute;
    const posArray = posAttr.array as Float32Array;
    const colorArray = colorAttr.array as Float32Array;
    const alphaArray = alphaAttr.array as Float32Array;
    const sizeArray = sizeAttr.array as Float32Array;

    for (let i = 0; i < count; i++) {
      const headSlot = headIndex[i];
      const base = (i * PARTICLE_TRAIL_LENGTH + headSlot) * 3;
      posArray[i * 3] = history[base];
      posArray[i * 3 + 1] = history[base + 1];
      posArray[i * 3 + 2] = history[base + 2];

      temperatureToColor(temperatureK[i], tmpColor);
      colorArray[i * 3] = tmpColor.r;
      colorArray[i * 3 + 1] = tmpColor.g;
      colorArray[i * 3 + 2] = tmpColor.b;

      alphaArray[i] = lifecycleAlpha[i] * altitudeOpacity[i] * PARTICLE_MAX_OPACITY;

      sizeArray[i] = headSizeForSpeedPx(horizontalSpeedMs[i]);
    }
    posAttr.needsUpdate = true;
    colorAttr.needsUpdate = true;
    alphaAttr.needsUpdate = true;
    sizeAttr.needsUpdate = true;
  }

  function writeTrailGeometry(devicePixelRatio: number, viewportWidthPx: number, viewportHeightPx: number): void {
    material.uniforms.devicePixelRatio.value = devicePixelRatio;
    material.uniforms.resolution.value.set(Math.max(1, viewportWidthPx), Math.max(1, viewportHeightPx));

    const olderPosAttr = geometry.getAttribute("instanceOlderPos") as THREE.InstancedBufferAttribute;
    const newerPosAttr = geometry.getAttribute("instanceNewerPos") as THREE.InstancedBufferAttribute;
    const colorOlderAttr = geometry.getAttribute("instanceColorOlder") as THREE.InstancedBufferAttribute;
    const colorNewerAttr = geometry.getAttribute("instanceColorNewer") as THREE.InstancedBufferAttribute;
    const alphaOlderAttr = geometry.getAttribute("instanceAlphaOlder") as THREE.InstancedBufferAttribute;
    const alphaNewerAttr = geometry.getAttribute("instanceAlphaNewer") as THREE.InstancedBufferAttribute;
    const widthAttr = geometry.getAttribute("instanceWidth") as THREE.InstancedBufferAttribute;
    const olderPosArray = olderPosAttr.array as Float32Array;
    const newerPosArray = newerPosAttr.array as Float32Array;
    const colorOlderArray = colorOlderAttr.array as Float32Array;
    const colorNewerArray = colorNewerAttr.array as Float32Array;
    const alphaOlderArray = alphaOlderAttr.array as Float32Array;
    const alphaNewerArray = alphaNewerAttr.array as Float32Array;
    const widthArray = widthAttr.array as Float32Array;

    for (let i = 0; i < count; i++) {
      const head = headIndex[i];
      // Combined fade-in/fade-out opacity, plus the overall opacity cap (see
      // PARTICLE_MAX_OPACITY) so trails blend slightly with the surface rather than reading as
      // solid, flat lines.
      const particleAlpha = lifecycleAlpha[i] * altitudeOpacity[i] * PARTICLE_MAX_OPACITY;
      // Trail width matches a fraction of this particle's current head marker size, so the
      // tail reads as a continuation of the head rather than a fixed-width line (see
      // TRAIL_WIDTH_FRACTION_OF_HEAD_SIZE in constants.ts).
      const widthPx = headSizeForSpeedPx(horizontalSpeedMs[i]) * TRAIL_WIDTH_FRACTION_OF_HEAD_SIZE;
      const particleSlotBase = i * PARTICLE_TRAIL_LENGTH;

      for (let age = 0; age < SEGMENTS_PER_PARTICLE; age++) {
        const newerSlot = (head - age + PARTICLE_TRAIL_LENGTH) % PARTICLE_TRAIL_LENGTH;
        const olderSlot = (head - age - 1 + PARTICLE_TRAIL_LENGTH) % PARTICLE_TRAIL_LENGTH;
        const newerBase = (particleSlotBase + newerSlot) * 3;
        const olderBase = (particleSlotBase + olderSlot) * 3;

        const instance = i * SEGMENTS_PER_PARTICLE + age;
        const vec3Base = instance * 3;
        newerPosArray[vec3Base] = history[newerBase];
        newerPosArray[vec3Base + 1] = history[newerBase + 1];
        newerPosArray[vec3Base + 2] = history[newerBase + 2];
        olderPosArray[vec3Base] = history[olderBase];
        olderPosArray[vec3Base + 1] = history[olderBase + 1];
        olderPosArray[vec3Base + 2] = history[olderBase + 2];

        // Each ring-buffer slot's color was already precomputed from the temperature recorded
        // AT that point in the trail's history when the slot was written (see historyColor
        // above) -- a trail visibly shows the thermal history it passed through, e.g. cooling
        // as it crosses a front, rather than repainting its whole length to one color every
        // frame. Just copied here, not recomputed.
        colorNewerArray[vec3Base] = historyColor[newerBase];
        colorNewerArray[vec3Base + 1] = historyColor[newerBase + 1];
        colorNewerArray[vec3Base + 2] = historyColor[newerBase + 2];
        colorOlderArray[vec3Base] = historyColor[olderBase];
        colorOlderArray[vec3Base + 1] = historyColor[olderBase + 1];
        colorOlderArray[vec3Base + 2] = historyColor[olderBase + 2];

        // An eased (square-root-like) curve keeps the trail visibly bright for most of its
        // length before tapering off, closer to the classic earth.nullschool look than a
        // straight linear fade (see AGE_FADE_CURVE).
        alphaNewerArray[instance] = AGE_FADE_CURVE[age] * particleAlpha;
        alphaOlderArray[instance] = AGE_FADE_CURVE[age + 1] * particleAlpha;
        widthArray[instance] = widthPx;
      }
    }
    olderPosAttr.needsUpdate = true;
    newerPosAttr.needsUpdate = true;
    colorOlderAttr.needsUpdate = true;
    colorNewerAttr.needsUpdate = true;
    alphaOlderAttr.needsUpdate = true;
    alphaNewerAttr.needsUpdate = true;
    widthAttr.needsUpdate = true;
  }

  function update(dtSeconds: number, config: ParticleUpdateConfig): void {
    const effectiveDt = dtSeconds * config.speedMultiplier;
    const lowAltKm = pressureToHeightM(config.maxHpa) / 1000;
    const highAltKm = pressureToHeightM(config.minHpa) / 1000;
    const smoothingBlend = Math.min(1, dtSeconds / ALTITUDE_SMOOTHING_TAU_SECONDS);
    const wzSmoothingBlend = Math.min(1, dtSeconds / WZ_SMOOTHING_TAU_SECONDS);
    const retireProbability = OVERLOAD_RETIRE_RATE_PER_SECOND * dtSeconds;

    // Rebuild the occupancy histogram periodically (not every frame -- see
    // DENSITY_GRID_RECOMPUTE_EVERY_N_FRAMES) from this frame's starting positions.
    if (frameCounter % DENSITY_GRID_RECOMPUTE_EVERY_N_FRAMES === 0) {
      densityGrid.recompute(latDeg, lonDeg, count);
    }

    for (let i = 0; i < count; i++) {
      if (fadingOut[i]) {
        // Frozen in place while its trail fades to invisible; once fully faded, jump to a new spot.
        fadeOutProgress[i] += dtSeconds / PARTICLE_FADE_OUT_SECONDS;
        if (fadeOutProgress[i] >= 1) {
          respawn(i, config.minHpa, config.maxHpa, config.heightExaggeration, config.verticalMotionExaggeration, true);
          // Just (re)spawned this frame -- invisible until the fade-in ramps up over subsequent frames.
          lifecycleAlpha[i] = 0;
        } else {
          lifecycleAlpha[i] = Math.max(0, 1 - fadeOutProgress[i]);
        }
        continue;
      }

      const latRad = latDeg[i] * DEG2RAD;
      const pressureHpa = heightMToPressureHpa(trueAltKm[i] * 1000);
      const wind = sampleWind(latDeg[i], lonDeg[i], pressureHpa);
      temperatureK[i] = wind.temperatureK;
      horizontalSpeedMs[i] = Math.sqrt(wind.uMs * wind.uMs + wind.vMs * wind.vMs);
      // Real GFS vertical velocity is noisy at grid scale (much more than wind/temperature);
      // low-pass filter it before integrating altitude so that noise isn't massively amplified
      // by vertical motion exaggeration later. See particles.ts module doc comment.
      smoothedWMs[i] += (wind.wMs - smoothedWMs[i]) * wzSmoothingBlend;

      const dLonDeg =
        ((wind.uMs * SIMULATION_TIME_SCALE) / (EARTH_RADIUS_M * Math.max(MIN_COS_LAT, Math.cos(latRad)))) *
        RAD2DEG *
        effectiveDt;
      const dLatDeg = ((wind.vMs * SIMULATION_TIME_SCALE) / EARTH_RADIUS_M) * RAD2DEG * effectiveDt;
      // Real (unexaggerated) vertical motion -- physics sampling must stay physically consistent.
      const dTrueAltKm = ((smoothedWMs[i] * SIMULATION_TIME_SCALE) / 1000) * effectiveDt;

      lonDeg[i] += dLonDeg;
      if (lonDeg[i] > 180) lonDeg[i] -= 360;
      if (lonDeg[i] < -180) lonDeg[i] += 360;
      latDeg[i] = Math.max(-89.5, Math.min(89.5, latDeg[i] + dLatDeg));
      trueAltKm[i] += dTrueAltKm;
      baselineAltKm[i] += (trueAltKm[i] - baselineAltKm[i]) * smoothingBlend;
      ageSeconds[i] += dtSeconds;

      if (fadingIn[i]) {
        fadeInProgress[i] += dtSeconds / PARTICLE_FADE_IN_SECONDS;
        if (fadeInProgress[i] >= 1) {
          fadingIn[i] = 0;
          fadeInProgress[i] = 1;
        }
      }
      lifecycleAlpha[i] = fadingIn[i] ? fadeInProgress[i] : 1;

      // Opacity fades toward MIN_ALTITUDE_OPACITY_FACTOR at the top of the currently selected
      // pressure range -- using the exaggerated DISPLAY altitude (not the true one) so opacity
      // always matches what's actually rendered on screen, even when vertical motion
      // exaggeration pushes a particle well above/below its true physical position.
      const displayAlt = displayAltKm(i, config.verticalMotionExaggeration);
      const altitudeT = Math.min(1, Math.max(0, (displayAlt - lowAltKm) / Math.max(1e-6, highAltKm - lowAltKm)));
      altitudeOpacity[i] = 1 - (1 - MIN_ALTITUDE_OPACITY_FACTOR) * altitudeT;

      // (Re-)grant a density-retirement immunity window scaled by how deep the LOCAL (not
      // cell-averaged) pressure is here -- see LOW_PRESSURE_PERSISTENCE_* in constants.ts. Taking
      // the max (not overwriting) means re-entering a strong zone before an earlier grant has
      // expired extends it, rather than resetting it shorter. Staggered across frames by
      // particle index (see PERSISTENCE_SAMPLE_STAGGER_FRAMES) -- grants last several seconds,
      // so sampling each particle a few times a second (not every frame) is plenty responsive.
      if (sampleMslpHpa && (i + frameCounter) % PERSISTENCE_SAMPLE_STAGGER_FRAMES === 0) {
        const localMslpHpa = sampleMslpHpa(latDeg[i], lonDeg[i]);
        const persistenceT = Math.min(
          1,
          Math.max(
            0,
            (LOW_PRESSURE_PERSISTENCE_START_HPA - localMslpHpa) /
              (LOW_PRESSURE_PERSISTENCE_START_HPA - LOW_PRESSURE_PERSISTENCE_FULL_HPA)
          )
        );
        if (persistenceT > 0) {
          const grantedSeconds = MIN_PERSISTENCE_SECONDS + (MAX_PERSISTENCE_SECONDS - MIN_PERSISTENCE_SECONDS) * persistenceT;
          persistenceSecondsRemaining[i] = Math.max(persistenceSecondsRemaining[i], grantedSeconds);
        }
      }
      persistenceSecondsRemaining[i] = Math.max(0, persistenceSecondsRemaining[i] - dtSeconds);

      const outOfBounds =
        trueAltKm[i] < lowAltKm - ALTITUDE_OUT_OF_BOUNDS_MARGIN_KM ||
        trueAltKm[i] > highAltKm + ALTITUDE_OUT_OF_BOUNDS_MARGIN_KM;
      const overloaded =
        densityGrid.isOverloaded(latDeg[i], lonDeg[i]) &&
        persistenceSecondsRemaining[i] <= 0 &&
        Math.random() < retireProbability;

      headIndex[i] = (headIndex[i] + 1) % PARTICLE_TRAIL_LENGTH;
      currentWorldPosition(i, config.heightExaggeration, config.verticalMotionExaggeration, tmpVec);
      // Track how long this particle has been resting at the terrain soft floor (see
      // currentWorldPosition()) so it can be retired gracefully instead of visually lingering
      // there indefinitely -- see GROUNDED_RETIRE_SECONDS.
      groundedSeconds[i] = lastPositionWasNearFloor ? groundedSeconds[i] + dtSeconds : 0;
      const grounded = groundedSeconds[i] > GROUNDED_RETIRE_SECONDS;
      const shouldRetire = ageSeconds[i] > PARTICLE_MAX_AGE_SECONDS || outOfBounds || overloaded || grounded;
      const slotIndex = i * PARTICLE_TRAIL_LENGTH + headIndex[i];
      const base = slotIndex * 3;
      history[base] = tmpVec.x;
      history[base + 1] = tmpVec.y;
      history[base + 2] = tmpVec.z;
      // Precomputed once here (not in writeTrailGeometry(), which runs every frame for every
      // still-unchanged slot) -- see historyColor above.
      temperatureToColor(temperatureK[i], tmpColor);
      historyColor[base] = tmpColor.r;
      historyColor[base + 1] = tmpColor.g;
      historyColor[base + 2] = tmpColor.b;

      if (shouldRetire) {
        fadingOut[i] = 1;
        fadeOutProgress[i] = 0;
      }
    }

    writeTrailGeometry(config.devicePixelRatio, config.viewportWidthPx, config.viewportHeightPx);
    writeHeadPoints(config.devicePixelRatio);
    frameCounter++;
  }

  return { object3D, update };
}
